import React from 'react';
import { Calendar, FileText, Send, Upload, Home, Trophy, CheckCircle2, Clock, Zap, Phone, PhoneCall } from 'lucide-react';
import { MetricTile } from './MetricTile';
import { motion } from 'framer-motion';
import { LoanStage } from '../data/loans';
import { OfficerData } from '../data/officers';
interface TodayScorecardProps {
  officer: OfficerData;
  onTileClick?: (stage: LoanStage) => void;
  onSpeedBonusClick?: () => void;
  onAnswerRateClick?: () => void;
  onDialsClick?: () => void;
}
export function TodayScorecard({
  officer,
  onTileClick,
  onSpeedBonusClick,
  onAnswerRateClick,
  onDialsClick
}: TodayScorecardProps) {
  const {
    todayTarget,
    todayPoints,
    todayCreditPulls,
    todayDocOuts,
    todaySubmissions,
    todayClosings
  } = officer;
  const remaining = Math.max(0, todayTarget - todayPoints);
  const beatGoal = todayPoints >= todayTarget;
  const percentRemaining = todayTarget > 0 ? Math.round(remaining / todayTarget * 100) : 0;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.1
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-699">
      <div className="bg-[#0a1e5e] px-4 py-3 flex items-center justify-between text-white" data-id="element-700">
        <div className="flex items-center gap-2" data-id="element-701">
          <Calendar className="w-5 h-5" data-id="element-702" />
          <h2 className="font-bold tracking-wide" data-id="element-703">TODAY'S SCORECARD</h2>
        </div>
        <span className="text-sm font-medium text-blue-200" data-id="element-704">May 14, 2025</span>
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-705">
        {/* Top Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-gray-200 gap-y-4 mb-6" data-id="element-706">
          <div className="text-center px-2 min-w-0" data-id="element-707">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-708">
              Today's Target
            </p>
            <p className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-709">
              {todayTarget}
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-710">
              Points
            </p>
          </div>
          <div className="text-center px-2 min-w-0" data-id="element-711">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-712">
              Remaining Today
            </p>
            <p className={`text-3xl font-extrabold leading-none ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-713">
              {remaining}
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-714">
              Points
            </p>
          </div>
          <div className="text-center px-2 min-w-0" data-id="element-715">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-716">
              Days Left
            </p>
            <p className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-717">
              17
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-718">
              In Month
            </p>
          </div>
          <div className="text-center px-2 min-w-0" data-id="element-719">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-720">
              Daily Target
            </p>
            <p className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-721">
              {todayTarget}
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-722">
              Pts / Day
            </p>
          </div>
        </div>

        <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4" data-id="element-723">
          Today's Performance
        </h3>

        {/* Metric Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-id="element-724">
          <MetricTile title="Call Answer Rate" points={0} count={officer.todayCallAnswerRate} countSuffix="%" countLabel="Answer Rate" icon={Phone} colorClass="text-teal-600" bgClass="bg-teal-100" hidePoints onClick={onAnswerRateClick} data-id="element-725" />
          <MetricTile title="Dials" points={0} count={officer.todayDials} countLabel="Dials" icon={PhoneCall} colorClass="text-indigo-600" bgClass="bg-indigo-100" hidePoints onClick={onDialsClick} data-id="element-726" />
          <MetricTile title="Credit Pulls" points={750} count={todayCreditPulls} countLabel="Pulls" icon={FileText} colorClass="text-blue-700" bgClass="bg-blue-100" onClick={onTileClick ? () => onTileClick('credit_pull') : undefined} data-id="element-727" />
          <MetricTile title="Doc Outs" points={500} count={todayDocOuts} countLabel="Doc Outs" icon={Send} colorClass="text-green-600" bgClass="bg-green-100" onClick={onTileClick ? () => onTileClick('doc_out') : undefined} data-id="element-728" />
          <MetricTile title="Submissions" points={500} count={todaySubmissions} countLabel="Submissions" icon={Upload} colorClass="text-purple-600" bgClass="bg-purple-100" onClick={onTileClick ? () => onTileClick('submission') : undefined} data-id="element-729" />
          <MetricTile title="Closings" points={250} count={todayClosings} countLabel="Closings" icon={Home} colorClass="text-orange-500" bgClass="bg-orange-100" onClick={onTileClick ? () => onTileClick('closing') : undefined} data-id="element-730" />
        </div>

        {/* Success Banner */}
        <div className="grid sm:grid-cols-2 gap-3 mb-4" data-id="element-731">
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3 min-w-0" data-id="element-732">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${beatGoal ? 'bg-green-600' : 'bg-[#0a1e5e]'}`} data-id="element-733">
              <Trophy className="w-6 h-6 text-white" data-id="element-734" />
            </div>
            <div className="min-w-0" data-id="element-735">
              <p className="text-xs font-bold text-gray-500 uppercase leading-tight" data-id="element-736">
                Total Points Today
              </p>
              <div className="flex items-baseline gap-1 flex-wrap" data-id="element-737">
                <span className={`text-3xl font-extrabold leading-none ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-738">
                  {todayPoints.toLocaleString()}
                </span>
                <span className="text-xs font-bold text-gray-500" data-id="element-739">POINTS</span>
              </div>
            </div>
          </div>
          {beatGoal ? <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 min-w-0" data-id="element-740">
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center shrink-0" data-id="element-741">
                <CheckCircle2 className="w-6 h-6 text-white" data-id="element-742" />
              </div>
              <p className="text-sm font-bold text-green-700 uppercase leading-tight" data-id="element-743">
                You Beat Today's Goal!
              </p>
            </div> : <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3 min-w-0" data-id="element-744">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0" data-id="element-745">
                <Zap className="w-6 h-6 text-white" data-id="element-746" />
              </div>
              <p className="text-sm font-bold text-blue-700 uppercase leading-tight" data-id="element-747">
                {remaining} pts to hit today's goal
              </p>
            </div>}
        </div>

        {/* Speed Bonus */}
        {(() => {
        const Wrapper: any = onSpeedBonusClick ? 'button' : 'div';
        return <Wrapper {...onSpeedBonusClick ? {
          type: 'button',
          onClick: onSpeedBonusClick,
          'aria-label': 'View speed bonus loans for today'
        } : {}} className={`group relative w-full bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-3 mb-6 transition-all text-left ${onSpeedBonusClick ? 'hover:border-orange-400 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400' : ''}`} data-id="element-748">
              <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-749">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shrink-0" data-id="element-750">
                  <Clock className="w-5 h-5 text-white" data-id="element-751" />
                </div>
                <div className="min-w-0" data-id="element-752">
                  <p className="text-sm font-bold text-[#0a1e5e] uppercase leading-tight" data-id="element-753">
                    Speed Bonus
                  </p>
                  <p className="text-xs text-gray-600 leading-tight mt-0.5" data-id="element-754">
                    Close a loan within 14 days of credit pull
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0" data-id="element-755">
                <div className="flex items-baseline gap-1 justify-end" data-id="element-756">
                  <span className="text-2xl font-extrabold text-orange-500 leading-none" data-id="element-757">
                    0
                  </span>
                  <span className="text-xs font-bold text-gray-500" data-id="element-758">PTS</span>
                </div>
                <p className="text-[10px] font-bold text-gray-500 uppercase mt-1" data-id="element-759">
                  Today
                </p>
              </div>
              {onSpeedBonusClick && <span className="absolute top-2 right-2 text-[10px] font-bold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider" data-id="element-760">
                  View loans →
                </span>}
            </Wrapper>;
      })()}

        {/* Footer Stats */}
        <div className="mt-auto grid grid-cols-2 divide-x divide-gray-200 pt-4 border-t border-gray-100" data-id="element-761">
          <div className="text-center" data-id="element-762">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1" data-id="element-763">
              Goal Remaining Today
            </p>
            <div className="flex items-baseline justify-center gap-1" data-id="element-764">
              <span className={`text-2xl font-extrabold ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-765">
                {remaining}
              </span>
              <span className="text-xs font-bold text-gray-400" data-id="element-766">POINTS</span>
            </div>
          </div>
          <div className="text-center" data-id="element-767">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1" data-id="element-768">
              % Of Goal Remaining
            </p>
            <span className={`text-2xl font-extrabold ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-769">
              {percentRemaining}%
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
}